/**
 * IV sedation page — hub + three treatment pages, driven by data/sedation.js.
 *
 * Deliberately quieter than the rest of the site: teal on white, left-aligned,
 * generous spacing, layered detail, no urgency devices.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Check, Info, ArrowRight, ShieldCheck, PoundSterling } from 'lucide-react';
import SEO from './SEO';
import PageHeader from './PageHeader';
import SiteFooter from './SiteFooter';
import Breadcrumbs from './Breadcrumbs';
import DirectAnswer from './DirectAnswer';
import LazyImage from './LazyImage';
import WhatsAppIcon from './WhatsAppIcon';
import { Button } from './ui/button';
import SedationDisclosure from './sedation/SedationDisclosure';
import SedationEnquiryForm from './sedation/SedationEnquiryForm';
import SedationLocal from './sedation/SedationLocal';
import SedationStickyCta from './sedation/SedationStickyCta';
import { PRACTICE, WHATSAPP_LINK } from '../data/practice';
import { PUBLIC_SITE_URL } from '../data/seo';
import { getSedationPage, SEDATION_AREAS } from '../data/sedation';
import {
  getSedationServiceLD,
  getSpeakableWebPageLD,
  getDentistClinicLD,
  getPersonClinicianLD,
  getBreadcrumbLD,
  getFAQPageLD,
} from '../data/structuredData';

const CRUMB = { hub: 'IV Sedation', wisdom: 'Wisdom Teeth Under Sedation', implants: 'Implants Under Sedation', phobia: 'Dental Phobia' };
const H2 = 'text-2xl md:text-3xl font-semibold text-rabbit';
const BODY = 'text-base md:text-lg leading-relaxed text-chocolate-malt';

const SedationPage = ({ pageKey }) => {
  const page = getSedationPage(pageKey);
  const crumbs = [
    { name: 'Home', to: '/' },
    ...(pageKey === 'hub' ? [] : [{ name: 'IV Sedation', to: '/iv-sedation-dentistry' }]),
    { name: CRUMB[pageKey] },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={page.title}
        description={page.description}
        path={page.path}
        keywords={page.keywords}
        structuredData={[
          getSedationServiceLD({ name: page.h1, description: page.description, path: page.path, priceFrom: 450, areasServed: SEDATION_AREAS }),
          getSpeakableWebPageLD({ name: page.title, description: page.description, path: page.path }),
          getDentistClinicLD(),
          getPersonClinicianLD(),
          getBreadcrumbLD([
            { name: 'Home', url: `${PUBLIC_SITE_URL}/` },
            ...(pageKey === 'hub' ? [] : [{ name: 'IV Sedation', url: `${PUBLIC_SITE_URL}/iv-sedation-dentistry` }]),
            { name: CRUMB[pageKey], url: `${PUBLIC_SITE_URL}${page.path}` },
          ]),
          getFAQPageLD(page.faqs),
        ]}
      />
      <PageHeader />

      <main className="pt-28 md:pt-32">
        {/* Hero + direct answer */}
        <section className="container mx-auto px-4 max-w-6xl">
          <Breadcrumbs items={crumbs} />
          <div className="mt-8 grid gap-10 lg:grid-cols-12 lg:gap-14">
            <div className="lg:col-span-7">
              <p className="sed-rise text-xs font-semibold uppercase tracking-[0.2em] text-[#006A71]">{page.eyebrow}</p>
              <h1 className="sed-rise mt-5 text-4xl sm:text-5xl font-semibold leading-[1.12] tracking-tight text-rabbit" style={{ animationDelay: '60ms' }} data-testid="sedation-h1">
                {page.h1}
              </h1>
              <div className="sed-rise mt-8" style={{ animationDelay: '120ms' }}>
                <DirectAnswer question={page.directAnswer.question} answer={page.directAnswer.answer} bullets={page.directAnswer.bullets} />
              </div>
              <div className="sed-rise mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap" style={{ animationDelay: '180ms' }}>
                <Button asChild size="lg" className="bg-[#006A71] text-white hover:bg-[#01A9B4]">
                  <a href="#sedation-enquiry" data-testid="sedation-hero-enquiry-btn">Check if sedation is right for you</a>
                </Button>
                <Button asChild size="lg" variant="outline" className="border-[#006A71]/35 text-[#006A71] hover:bg-[#006A71]/5">
                  <a href={PRACTICE.phoneTel} data-testid="sedation-hero-call-btn"><Phone className="mr-2 h-5 w-5" strokeWidth={1.8} aria-hidden="true" />{PRACTICE.phoneDisplay}</a>
                </Button>
                <Button asChild size="lg" variant="outline" className="border-[#25D366]/50 text-[#128C4A] hover:bg-[#25D366]/10">
                  <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" data-testid="sedation-hero-whatsapp-btn"><WhatsAppIcon className="mr-2 h-5 w-5" aria-hidden="true" />WhatsApp us</a>
                </Button>
              </div>
              <p className="mt-6 max-w-2xl text-sm leading-relaxed text-chocolate-malt/75">
                Clinical content reviewed by <Link to="/about-dr-kartik-patel" className="underline underline-offset-4 text-[#006A71]">Dr Kartik Patel BDS</Link>, GDC {PRACTICE.gdcNumber} · {PRACTICE.addressOneLine} · We reply within one working day, Monday to Saturday.
              </p>
            </div>
            <div className="lg:col-span-5">
              <div className="sed-rise overflow-hidden rounded-3xl border border-[#006A71]/12 bg-[#006A71]/[0.04]" style={{ animationDelay: '220ms' }}>
                <LazyImage
                  src={page.image.src}
                  alt={page.image.alt}
                  width="900"
                  height="1100"
                  loading="eager"
                  className="h-64 w-full object-cover sm:h-80 lg:h-[30rem]"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Suitability enquiry — top */}
        <section id="sedation-enquiry" className="container mx-auto px-4 max-w-5xl mt-20 md:mt-28 scroll-mt-28">
          <SedationEnquiryForm idPrefix="top" source={page.path} intro={page.enquiryIntro} />
        </section>

        {/* What it feels like */}
        <section className="container mx-auto px-4 max-w-5xl mt-20 md:mt-28">
          <h2 className={H2}>{page.feels.heading}</h2>
          <p className={`mt-4 max-w-3xl ${BODY}`}>{page.feels.intro}</p>
          <div className="mt-10 grid gap-8 sm:grid-cols-2 lg:gap-x-14">
            {page.feels.points.map((p) => (
              <div key={p.title} className="border-l-2 border-[#01A9B4]/35 pl-6">
                <h3 className="text-lg font-semibold text-rabbit">{p.title}</h3>
                <p className="mt-2.5 text-base leading-relaxed text-chocolate-malt">{p.body}</p>
              </div>
            ))}
          </div>
          <p className="mt-10 max-w-3xl rounded-2xl bg-[#006A71]/[0.05] p-6 text-base leading-relaxed text-chocolate-malt">{page.feels.reassure}</p>
        </section>

        {/* One appointment */}
        <section className="container mx-auto px-4 max-w-5xl mt-20 md:mt-28">
          <h2 className={H2}>{page.oneAppointment.heading}</h2>
          <p className={`mt-4 max-w-3xl ${BODY}`}>{page.oneAppointment.intro}</p>
          <ul className="mt-8 space-y-3.5">
            {page.oneAppointment.items.map((item) => (
              <li key={item} className="flex gap-4 text-base leading-relaxed text-chocolate-malt">
                <Check className="mt-1 h-5 w-5 flex-shrink-0 text-[#006A71]" strokeWidth={2} aria-hidden="true" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <p className="mt-7 max-w-3xl text-base leading-relaxed text-chocolate-malt/85">{page.oneAppointment.note}</p>
        </section>

        {/* Who it suits */}
        <section className="container mx-auto px-4 max-w-5xl mt-20 md:mt-28">
          <h2 className={H2}>{page.suits.heading}</h2>
          <div className="mt-9 grid gap-8 md:grid-cols-2 lg:gap-12">
            <div className="rounded-2xl border border-[#006A71]/15 p-7">
              <h3 className="text-lg font-semibold text-rabbit">It usually suits</h3>
              <ul className="mt-5 space-y-3.5">
                {page.suits.yes.map((item) => (
                  <li key={item} className="flex gap-3.5 text-base leading-relaxed text-chocolate-malt">
                    <Check className="mt-1 h-5 w-5 flex-shrink-0 text-[#006A71]" strokeWidth={2} aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border border-[#D4AF37]/35 bg-[#D4AF37]/[0.05] p-7">
              <h3 className="text-lg font-semibold text-rabbit">When it is not the right option</h3>
              <ul className="mt-5 space-y-3.5">
                {page.suits.caution.map((item) => (
                  <li key={item} className="flex gap-3.5 text-base leading-relaxed text-chocolate-malt">
                    <Info className="mt-1 h-5 w-5 flex-shrink-0 text-[#8a6d1f]" strokeWidth={1.9} aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <p className="mt-8 max-w-3xl text-base leading-relaxed text-chocolate-malt/85">{page.suits.note}</p>
        </section>

        {/* Layered detail: the day, and safety */}
        <section className="container mx-auto px-4 max-w-5xl mt-20 md:mt-28 space-y-5">
          <SedationDisclosure heading={page.day.heading} summary={page.day.summary} tid="sedation-day">
            <ol className="space-y-7">
              {page.day.steps.map((s, i) => (
                <li key={s.title} className="flex gap-5">
                  <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[#006A71]/10 text-sm font-semibold text-[#006A71]" aria-hidden="true">{i + 1}</span>
                  <div>
                    <h3 className="text-lg font-semibold text-rabbit">{s.title}</h3>
                    <p className="mt-2 text-base leading-relaxed text-chocolate-malt">{s.body}</p>
                  </div>
                </li>
              ))}
            </ol>
          </SedationDisclosure>

          <SedationDisclosure heading={page.safety.heading} summary={page.safety.summary} tid="sedation-safety">
            <div className="grid gap-7 sm:grid-cols-2">
              {page.safety.points.map((p) => (
                <div key={p.title} className="flex gap-4">
                  <ShieldCheck className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#006A71]" strokeWidth={1.8} aria-hidden="true" />
                  <div>
                    <h3 className="text-base font-semibold text-rabbit">{p.title}</h3>
                    <p className="mt-1.5 text-base leading-relaxed text-chocolate-malt">{p.body}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-7 text-sm text-chocolate-malt/75">
              Check our registration on the{' '}
              <a href={PRACTICE.links.gdcRegister} target="_blank" rel="noopener noreferrer" className="underline underline-offset-4 text-[#006A71]">GDC register</a>.
            </p>
          </SedationDisclosure>
        </section>

        {/* Fees */}
        <section className="container mx-auto px-4 max-w-5xl mt-20 md:mt-28">
          <h2 className={H2}>{page.fees.heading}</h2>
          <p className={`mt-4 max-w-3xl ${BODY}`}>{page.fees.intro}</p>
          <dl className="mt-9 divide-y divide-[#006A71]/12 overflow-hidden rounded-2xl border border-[#006A71]/15" data-testid="sedation-fees">
            {page.fees.rows.map(([label, price, note]) => (
              <div key={label} className="flex flex-col gap-1 px-6 py-5 sm:flex-row sm:items-baseline sm:justify-between sm:gap-8">
                <div>
                  <dt className="text-base font-semibold text-rabbit">{label}</dt>
                  <dd className="mt-1 text-sm text-chocolate-malt/80">{note}</dd>
                </div>
                <dd className="whitespace-nowrap text-lg font-semibold text-[#006A71]">{price}</dd>
              </div>
            ))}
          </dl>
          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-6">
            <p className="flex items-start gap-3 text-sm leading-relaxed text-chocolate-malt/85">
              <PoundSterling className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#006A71]" strokeWidth={2} aria-hidden="true" />
              Every patient gets a fixed written quote after the assessment. 0% finance over 3–60 months is available on treatment over £500.
            </p>
            <div className="flex gap-5 text-sm font-semibold">
              <Link to="/finance" className="whitespace-nowrap text-[#006A71] underline underline-offset-4 hover:text-[#01A9B4]">Finance calculator</Link>
              <Link to="/pricing" className="whitespace-nowrap text-[#006A71] underline underline-offset-4 hover:text-[#01A9B4]">Full fee guide</Link>
            </div>
          </div>
        </section>

        {/* Local + getting home */}
        <SedationLocal local={page.local} gettingHome={page.gettingHome} />

        {/* FAQs */}
        <section className="container mx-auto px-4 max-w-3xl mt-20 md:mt-28">
          <h2 className={H2}>Your questions, answered plainly</h2>
          <div className="mt-9 divide-y divide-[#006A71]/12" data-testid="sedation-faqs">
            {page.faqs.map((f) => (
              <div key={f.question} className="py-7 first:pt-0">
                <h3 className="text-lg font-semibold text-rabbit">{f.question}</h3>
                <p className="mt-2.5 text-base leading-relaxed text-chocolate-malt">{f.answer}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Related */}
        <section className="container mx-auto px-4 max-w-5xl mt-20 md:mt-28">
          <h2 className={H2}>Where to next</h2>
          <nav aria-label="Related pages" className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {page.related.map((r) => (
              <Link
                key={r.to}
                to={r.to}
                className="group rounded-2xl border border-[#006A71]/15 p-6 transition-colors duration-200 hover:border-[#01A9B4]/55 hover:bg-[#01A9B4]/[0.04] motion-reduce:transition-none"
                data-testid={`sedation-related-${r.to.replace(/\//g, '-').replace(/^-/, '')}`}
              >
                <span className="flex items-center gap-2 text-base font-semibold text-rabbit group-hover:text-[#006A71]">
                  {r.label}
                  <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1 motion-reduce:transition-none" strokeWidth={1.9} aria-hidden="true" />
                </span>
                <span className="mt-2 block text-sm leading-relaxed text-chocolate-malt">{r.body}</span>
              </Link>
            ))}
          </nav>
        </section>

        {/* Suitability enquiry — bottom */}
        <section className="container mx-auto px-4 max-w-5xl mt-20 md:mt-28 mb-24 md:mb-32">
          <SedationEnquiryForm idPrefix="bottom" source={page.path} intro={page.enquiryIntro} />
        </section>
      </main>

      <SiteFooter />
      <SedationStickyCta />
    </div>
  );
};

export default SedationPage;
